import { memo, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import DOMPurify from "dompurify"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { ThreadSearchDocument } from "@productlane/search"
import type { FeedbackSearchResult } from "@productlane/zero/queries"
import { cn, convertHtmlToText } from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { Avatar, Tooltip } from "@productlane/ui"

import { UserCompanyAvatar } from "@/screens/app/customers/user-company-avatar"
import { useLastThreadMessage } from "@/screens/app/suggestion/use-thread-message"
import { isEmptyContentTag } from "@/screens/app/threads/helpers"

interface Props {
	thread: FeedbackSearchResult
	className?: string
	contactName?: ThreadSearchDocument["person"]
	companyName?: ThreadSearchDocument["company"]
	title?: ThreadSearchDocument["title"]
	text?: ThreadSearchDocument["text"]
}

export const SearchListItem = memo(
	({ thread, className, contactName, companyName, title, text }: Props) => {
		const lastMessageText = useLastThreadMessage(thread, "desc")

		const convertedLastMessage = useMemo(() => {
			const htmlContent =
				lastMessageText ||
				(!isEmptyContentTag(thread?.text) ? (thread?.text ?? "") : "")
			return convertHtmlToText(htmlContent)
		}, [thread?.text, lastMessageText])

		const sanitizedText = useMemo(
			() =>
				text
					? DOMPurify.sanitize(text, {
							ALLOWED_TAGS: ["span", "mark", "em", "strong"],
						})
					: null,
			[text],
		)

		if (!thread) return null

		return (
			<Link
				to="/threads/$id"
				params={{ id: thread.id }}
				className={cn(
					"box-content block select-none text-sm hover:bg-gray-100 dark:hover:bg-gray-850",
					className,
				)}
			>
				<div
					className={cn(
						"items-between flex px-1 pr-6 pt-2.5 sm:py-3",
						className,
					)}
				>
					<div className="flex w-1/5 min-w-52 flex-1 sm:flex-none">
						<div className="group ml-2 flex h-11 w-14 flex-none items-center justify-center sm:ml-3">
							<div className="flex">
								<UserCompanyAvatar
									brandfetchData={
										thread?.company?.companyMeta
											?.brandfetchData as unknown as BrandfetchData
									}
									name={thread?.contact?.name || thread?.contact?.email}
									colorSeed={thread?.contact?.email}
									image={thread?.contact?.imageUrl}
									thread={thread}
								/>
							</div>
						</div>

						{/* Name and Company */}
						<div className="mr-5 flex h-11 min-w-36 flex-1 flex-col items-start justify-center truncate sm:flex-none">
							{thread?.contact ? (
								<div
									className="max-w-36 truncate font-medium"
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(
											contactName ||
												(thread?.contact?.name ?? thread?.contact?.email),
										),
									}}
								/>
							) : (
								<div className="text-tertiary">No customer</div>
							)}
							{!!thread?.company && (
								<div className="flex max-w-36">
									<span
										className="truncate text-tertiary"
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(
												companyName || thread?.company?.name,
											),
										}}
									/>
								</div>
							)}
						</div>
					</div>

					{/* Title and Description (Medium) */}
					<div className="hidden flex-1 flex-col justify-center truncate sm:flex">
						<div className="flex flex-row items-center justify-between">
							{!!thread.title && (
								<div
									className="truncate font-medium"
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(title || thread.title),
									}}
								/>
							)}
						</div>
						{sanitizedText ? (
							<div
								className="truncate pr-10 text-xs text-tertiary"
								dangerouslySetInnerHTML={{ __html: sanitizedText }}
							/>
						) : (
							<div className="truncate pr-10 text-xs text-tertiary">
								{convertedLastMessage}
							</div>
						)}
					</div>

					{/* Assignee */}
					<div className="ml-4 flex min-w-5 flex-none items-center justify-end">
						<div className="mr-2 text-xs text-tertiary">
							{formatRelativeTime(new Date(thread.createdAt))}
						</div>
						<Tooltip
							label={thread?.assignee?.name ?? "No assignee"}
							shouldWrapChildren
						>
							<Avatar
								showAvatarFallback={!thread?.assignee}
								{...(!thread?.assignee
									? {
											fallbackProps: { className: "text-white" },
										}
									: {})}
								image={thread?.assignee?.imageUrl}
								name={
									thread?.assignee?.name
										? thread?.assignee?.name
										: (thread?.assignee?.email ?? "No assignee")
								}
								size="xs"
								roundness="full"
							/>
						</Tooltip>
					</div>
				</div>

				{/* Title and Description (Small) */}
				<div className="flex pb-4 pr-6 sm:hidden">
					<div className="ml-2 w-14 flex-none" />
					<div className="flex-1 truncate">
						{thread.title && (
							<span
								className="mr-5 flex truncate font-medium"
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(title || thread.title),
								}}
							/>
						)}
						{sanitizedText ? (
							<div
								className="truncate pr-10 text-xs text-tertiary"
								dangerouslySetInnerHTML={{ __html: sanitizedText }}
							/>
						) : (
							<div className="truncate pr-10 text-xs text-tertiary">
								{convertedLastMessage}
							</div>
						)}
					</div>
				</div>
			</Link>
		)
	},
)
